import { searchKey } from './searchKey';

// ── WHAT MAY NOT BE SENT ─────────────────────────────────────────────────────
// Tunify's chats put customers in touch with designers and shops they have
// never met, and the messages are not moderated by anyone. A short block list
// is not a solution to abuse, but it stops the casual case — the insult typed
// in temper — and it costs one function call on send.
//
// ONE LIST FOR THREE ALPHABETS. Every entry below is reduced by `searchKey`,
// the same transliteration the search box uses, and so is every word of the
// message. So `boz`, `բոզ` and `боз` are one entry, not three, and a reader
// swapping keyboards mid-sentence is caught by the same rule.
//
// ── WHOLE WORDS, NEVER SUBSTRINGS, AND THIS IS NOT A STYLE CHOICE ────────────
// The Russian root `хуй` reduces to `hui`. The Armenian for FURNITURE —
// `կահույք`, the single most-typed noun in this app — reduces to `kahuik`,
// which contains it. Substring matching would refuse to let anybody discuss
// furniture on a furniture marketplace.
//
// So a word matches only if the WHOLE word matches, with a short list of
// suffixes stripped first so `fucking` and `bitches` are caught without
// `kahuik` ever being looked at.
//
// ── WHAT THIS IS NOT ─────────────────────────────────────────────────────────
// It is not a moderation system. It runs in the browser, so anyone willing to
// open a console can bypass it entirely — see the note in chatService where it
// is called. It catches typing, not determination.

// Readable source, folded once at load. Kept as words rather than pre-folded
// keys so the list can be edited by someone who does not know what folding is.
const WORDS = [
  // ── Armenian ──
  'boz', 'բոզ',
  'qunem', 'քունեմ', 'kunem', 'կունեմ',
  'gnadon', 'գնդոն',
  'mayrud', 'մայրդ', 'mayrt',
  'kues', 'կուես',
  'pox', 'պոռնիկ', 'pornik',
  'txmot',

  // ── Russian ──
  'сука', 'suka', 'суки', 'сучка',
  'блядь', 'blyad', 'блять', 'бля',
  'хуй', 'hui', 'huy', 'хуё', 'хуе',
  'пизда', 'pizda', 'пиздец',
  'ебать', 'ebat', 'ебал', 'ёбаный', 'ебаный',
  'мудак', 'mudak',
  'пидор', 'pidor', 'pidr', 'пидр', 'пидорас',
  'гандон', 'гондон',
  'шлюха', 'shluha',
  'долбоёб', 'долбоеб',

  // ── English ──
  // `fack` is here because leet maps `4` to A, not U — so `f4ck` arrives as
  // `fack` and would otherwise sail through. It is also a common deliberate
  // misspelling in its own right, and not a word in any of the three languages.
  'fuck', 'fuk', 'fck', 'fack', 'fuckin',
  'shit',
  'bitch',
  'cunt',
  'asshole', 'arsehole',
  'bastard',
  'whore',
  'slut',
  'dickhead',
  'motherfucker',
  'nigger', 'nigga',
  'faggot', 'fag',
  'retard',
];

// ── LEETSPEAK ────────────────────────────────────────────────────────────────
// Applied BEFORE folding, because `b1tch` has to become `bitch` before the
// alphabet tables see it. Only the substitutions people actually use; a
// broader table starts turning ordinary text into words that were never typed.
const LEET = { 0: 'o', 1: 'i', 3: 'e', 4: 'a', 5: 's', 7: 't', 8: 'b', '@': 'a', $: 's' };

// Stripped from the end of a word before it is looked up, so one entry covers
// its inflections. Longest first — `ings` must be tried before `ing`, or
// `fuckings` reduces to `fucking` and misses.
const SUFFIXES = ['ings', 'ings', 'ing', 'ers', 'er', 'ies', 'es', 'ed', 's', 'y'];

const key = (w) => searchKey(String(w).replace(/[0134578@$]/g, (c) => LEET[c] ?? c));

const BLOCKED = new Set(WORDS.map(key).filter(Boolean));

/** Does this single word — already leet-mapped and folded — match the list? */
function wordIsBlocked(k) {
  if (!k) return false;
  if (BLOCKED.has(k)) return true;
  for (const suffix of SUFFIXES) {
    if (k.length > suffix.length + 2 && k.endsWith(suffix)) {
      if (BLOCKED.has(k.slice(0, -suffix.length))) return true;
    }
  }
  return false;
}

/**
 * The first blocked word in `text`, or `''`.
 *
 * Returns the OFFENDING WORD rather than a boolean so the caller can name it —
 * "that word is not allowed" is a message somebody can act on, where "your
 * message was blocked" invites them to retype the same sentence.
 */
export function findProfanity(text) {
  const raw = String(text || '');
  if (!raw.trim()) return '';

  // Split on anything that is not a letter or a digit, so punctuation cannot
  // hide a word: `fuck!`, `(bitch)` and `f***` all separate the same way.
  // `\p{L}` covers Armenian and Cyrillic as well as Latin.
  const tokens = raw.split(/[^\p{L}\p{N}]+/u).filter(Boolean);

  for (const token of tokens) {
    if (wordIsBlocked(key(token))) return token;
  }

  // ── LETTERS SPACED OUT TO GET PAST THE LIST ────────────────────────────────
  // `f u c k` is four tokens, none of which is a word. Runs of THREE OR MORE
  // single-letter tokens are joined and tested as one.
  //
  // Narrow on purpose: a run of single letters is not something people type by
  // accident, and requiring three of them keeps ordinary initials — "J R R
  // Tolkien" is three, so the join is TESTED, not blocked, and only a hit
  // matters.
  let run = [];
  for (const token of [...tokens, '']) {
    if (token.length === 1) { run.push(token); continue; }
    if (run.length >= 3 && wordIsBlocked(key(run.join('')))) return run.join(' ');
    run = [];
  }

  return '';
}

/** Convenience for callers that only need to know whether to stop. */
export function hasProfanity(text) {
  return findProfanity(text) !== '';
}

// Exported for the tests, which assert the list folds to what it claims to.
export const BLOCKED_KEYS = BLOCKED;
